"use client";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X, Trash2, Check } from "lucide-react";

interface SortableItemProps {
  id: string;
  imageUrl: string | null;
  name: string;
  onDelete: () => void;
  deleteIcon?: "remove" | "delete";
  deleteLabel?: string;
  isEditMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (itemId: string, isShiftClick: boolean) => void;
}

export function SortableItem({
  id,
  imageUrl,
  name,
  onDelete,
  deleteIcon = "remove",
  deleteLabel = "Remove item",
  isEditMode = false,
  isSelected = false,
  onToggleSelection,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: isEditMode, // Disable dragging in edit mode
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const DeleteIcon = deleteIcon === "delete" ? Trash2 : X;

  const handleClick = (e: React.MouseEvent) => {
    if (isEditMode && onToggleSelection) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelection(id, e.shiftKey);
    }
  };

  if (isEditMode) {
    // Edit mode: clickable with checkbox
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="relative group cursor-pointer"
        onClick={handleClick}
      >
        <div
          style={{
            width: 'calc(var(--tiers-root-size) * 5)',
            height: 'calc(var(--tiers-root-size) * 5)',
          }}
          className={`rounded overflow-hidden bg-card border-2 transition-all relative ${
            isSelected
              ? "border-blue-500 ring-2 ring-blue-500 ring-opacity-50"
              : "border-border hover:border-ring"
          }`}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt={name}
              className={`w-full h-full object-cover transition-opacity ${
                isSelected ? "opacity-60" : ""
              }`}
            />
          )}
          {/* Gray overlay on hover (under checkbox) */}
          <div className="absolute inset-0 bg-gray-600/50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
          {/* Checkbox overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              style={{
                width: 'calc(var(--tiers-root-size) * 2)',
                height: 'calc(var(--tiers-root-size) * 2)',
              }}
              className={`rounded-full flex items-center justify-center transition-all ${
                isSelected
                  ? "bg-blue-500 scale-100"
                  : "bg-gray-700/50 scale-0 group-hover:scale-100"
              }`}
            >
              {isSelected && (
                <Check
                  style={{
                    width: 'calc(var(--tiers-root-size) * 1.25)',
                    height: 'calc(var(--tiers-root-size) * 1.25)',
                  }}
                  className="text-white"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal mode: draggable with delete button
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group cursor-move"
    >
      <div
        style={{
          width: 'calc(var(--tiers-root-size) * 5)',
          height: 'calc(var(--tiers-root-size) * 5)',
        }}
        className="rounded overflow-hidden bg-card border-2 border-border hover:border-ring transition-colors"
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        style={{
          top: 'calc(var(--tiers-root-size) * -0.5)',
          right: 'calc(var(--tiers-root-size) * -0.5)',
          padding: 'calc(var(--tiers-root-size) * 0.25)',
        }}
        className="absolute bg-destructive hover:bg-destructive/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
        aria-label={deleteLabel}
      >
        <DeleteIcon
          style={{
            width: 'calc(var(--tiers-root-size) * 0.75)',
            height: 'calc(var(--tiers-root-size) * 0.75)',
          }}
        />
      </button>
    </div>
  );
}
